import {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { CSSProperties } from 'react';
import { Play } from 'lucide-react';
import { useSetAtom } from 'jotai';
import { useLoaderData, useLocation, useSearchParams } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { Image } from '@rocco/components/image';
import { useTracker } from '@archipro-website/tracker';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import getGallerySlides from '@modules/product/util/get-gallery-slides';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import type { LibraryVideo } from '@modules/product/type/product-gallery-types';
import { cn } from '@rocco/utils/cn';
import VideoSlide from '@modules/product/component/photo-gallery/VideoSlide';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { useFeature } from '@modules/root';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useProductEnquiryModal } from '../hooks/use-product-enquiry-modal';
import {
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { CarouselPaginationPill } from '~/ui-rocco/pages/home-page/components/CarouselPaginationPill';
import { useProductDetailHeroStyle } from '@modules/product/util/product-hero-view-transition';
import { getPendingHeroImage } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { photoViewerTargetAtom } from '../photo-viewer';

/** Lazy-loaded photo viewer — keeps the heavy lightbox out of the page chunk. */
const PhotoViewerModal = lazy(
    () => import('@modules/product/component/photo-gallery/PhotoViewerModal'),
);

/**
 * Responsive sizes for a gallery slide — 93.6rem (≈1498px at the 1920 anchor)
 * on desktop, 22.2525rem (≈356px at the 414 anchor) on mobile. Shared with the
 * route's preload link so the browser reuses the preloaded first slide instead
 * of fetching a second variant (the cause of the grey flash on load).
 */
export const PRODUCT_GALLERY_SLIDE_SIZES = getImagesSizes({
    desktop: 1498,
    mobile: 414,
});

/** Autoplay cadence — the duration of the island's progress fill / dwell per slide. */
const GALLERY_AUTOPLAY_DELAY = 4000;

/**
 * Slides share a fixed height and take their width from the canonical shape the
 * image snaps to (variable-width, consistent-height row). 54.375rem ≈ 870px at
 * our 1rem = 16px base.
 */
const GALLERY_SLIDE_HEIGHT_REM = 54.375;
/**
 * The same variable-width / consistent-height row on mobile, sized in vw so the
 * square card sits a little inside the viewport — the next slide then peeks at
 * the edge (a single slide instead stretches to the page gutter). Landscape
 * images are cropped to a square card (see the gallery `fit`) rather than
 * letterboxed, so the mobile row only ever shows square or narrower (portrait)
 * widths — never a bar.
 */
const GALLERY_SLIDE_HEIGHT_MOBILE_VW = 84;
/** Mobile card width for a slide: height × ratio, clamped to square so
 *  landscapes don't produce a letterboxed wide card. */
const mobileSlideWidthVw = (ratio: number) =>
    GALLERY_SLIDE_HEIGHT_MOBILE_VW * Math.min(ratio, 1);
/**
 * Each product image is served in one of three canonical shapes by orientation —
 * portrait 2:3, square 1:1, landscape 3:2 — so the gallery shows at most three
 * slide widths. The thresholds + ratios must match `getCanonicalProductRatio` on
 * the server so the CSS box matches the rendition (no letterbox/crop). Videos
 * aren't product images (no snap rendition), so they keep a native 16:9 frame.
 */
const GALLERY_PORTRAIT_RATIO = 2 / 3;
const GALLERY_SQUARE_RATIO = 1;
const GALLERY_LANDSCAPE_RATIO = 3 / 2;
const GALLERY_LANDSCAPE_THRESHOLD = 1.25;
const GALLERY_PORTRAIT_THRESHOLD = 0.833;
const GALLERY_VIDEO_RATIO = 16 / 9;

/** The canonical display ratio (width ÷ height) a gallery slide snaps to. */
const getSlideRatio = (slide: ReturnType<typeof getGallerySlides>[number]) => {
    if (isLibraryVideo(slide)) return GALLERY_VIDEO_RATIO;
    const ratio = slide.AspectRatio || 1;
    if (ratio >= GALLERY_LANDSCAPE_THRESHOLD) return GALLERY_LANDSCAPE_RATIO;
    if (ratio <= GALLERY_PORTRAIT_THRESHOLD) return GALLERY_PORTRAIT_RATIO;
    return GALLERY_SQUARE_RATIO;
};

/**
 * The stable `/images/s1/…/file.ext` portion of a rendition URL — tile and
 * gallery renditions of the same photo differ by CDN prefix and transform
 * suffix, so equality is only meaningful on this segment.
 */
const galleryImageKey = (url: string): string | null =>
    url.match(/\/images\/s1\/.+?\.(?:jpe?g|png|webp|gif)/i)?.[0] ?? null;

/** True when the Magic Island is active (flag on, or dev `?detailsRevamp=1`). */
const useIslandEnabled = () => {
    const { detailsRevamp } = useLoaderData<typeof loader>();
    const [searchParams] = useSearchParams();
    return (
        detailsRevamp ||
        (import.meta.env.DEV && searchParams.get('detailsRevamp') === '1')
    );
};

const GalleryPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    // When the Magic Island is on it owns the pagination, so the in-page pill
    // hides — otherwise the gallery would show two pagination controls.
    if (useIslandEnabled()) return null;

    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-3 md:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Product gallery"
            />
        </div>
    );
};

/**
 * B1 — video slide with an always-visible play indicator. The indicator needs
 * `z-10` because the aria VideoPlayer's internals stack above plain siblings,
 * and it hides while the video plays (VideoSlide reports playback via
 * `onPlayingChange`). `pointer-events-none` keeps clicks flowing to the player.
 */
const GalleryVideoSlide = ({ slide }: { slide: LibraryVideo }) => {
    const [playing, setPlaying] = useState(false);

    return (
        <div className="absolute inset-0">
            <VideoSlide
                libraryVideo={slide}
                onPlayingChange={setPlaying}
                hideNativePlayIcon
            />
            <div
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-200',
                    playing ? 'opacity-0' : 'opacity-100',
                )}
            >
                <div className="flex size-16 items-center justify-center rounded-full bg-black/40 md:size-24">
                    <Play
                        className="size-6 fill-white text-white md:size-9"
                        strokeWidth={0}
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * F3 — editorial gallery.
 *
 * Large centre-aligned carousel with adjacent peeks, mirroring the homepage
 * Discover Projects banner treatment. The slide stack matches the legacy
 * gallery — images + videos (sorted by member order) then diagrams, via
 * `getGallerySlides`. Videos play inline (legacy `VideoSlide`, with its
 * 'Video' tracking); tapping an image logs `PhotoViewerTrigger` and opens a
 * photo viewer at that slide — the legacy lightbox on desktop (kept in sync
 * with the carousel both ways), the full-bleed pinch-to-zoom viewer on mobile.
 * Award badges overlay the gallery behind the `award_badges` flag. Hidden when
 * the product has no media.
 */
export const ProductGallerySection = () => {
    const { product } = useLoaderData<typeof loader>();
    const professional = product.Professional;
    const tracker = useTracker();
    // Each breakpoint has its own viewer: the desktop lightbox below, and the
    // full-bleed pinch-to-zoom viewer the page mounts for mobile.
    const { mobile } = useAppDisplayModeContext();
    const openMobileViewer = useSetAtom(photoViewerTargetAtom);
    const islandEnabled = useIslandEnabled();
    // Slide 0 is the counterpart of the listing tile the user clicked, so it
    // carries the shared snapshot name for the arriving transition.
    const heroStyle = useProductDetailHeroStyle();
    // The tile rendition the user clicked (already decoded) — painted under
    // slide 0's own image so a cold cache shows the same pixels sharpening
    // instead of a grey placeholder. Latched at mount; null on full loads.
    const { pathname } = useLocation();
    const [heroUnderlaySrc] = useState(() => getPendingHeroImage(pathname));
    // The underlay is the tile's SQUARE crop; covered into a landscape slide
    // box it reads noticeably zoomed-in, so the real rendition dissolves in
    // over it on load instead of popping to the wider framing.
    const [heroImageLoaded, setHeroImageLoaded] = useState(false);
    const markHeroImageLoaded = useCallback(() => setHeroImageLoaded(true), []);

    const [viewerOpen, setViewerOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);

    // Track the settled slide so the neighbours of wherever the user scrolls
    // to flip from lazy to eager before they peek into view.
    const [currentSlide, setCurrentSlide] = useState(0);
    // Whether autoplay has stopped on the last slide (so the island shows a
    // replay control). Derived from the same live embla snap count `advance`
    // uses, so it can't disagree with the stop logic — the dot index
    // (`stack.length`) and embla's snap count can differ (e.g. mobile
    // `trimSnaps`), which would otherwise strand the carousel stopped with a
    // non-functional play/pause button. Only `advance` sets it true; settling
    // off the last slide (drag-back, dot nav) clears it.
    const [reachedEnd, setReachedEnd] = useState(false);
    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => {
            setCurrentSlide(emblaApi.selectedScrollSnap());
            if (
                emblaApi.selectedScrollSnap() <
                emblaApi.scrollSnapList().length - 1
            ) {
                setReachedEnd(false);
            }
        };
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('reInit', onSelect);
        };
    }, [emblaApi]);

    const enableAwardBadges = useFeature('award_badges');

    // Diagrams are excluded from the gallery — they live in the (02) Specs &
    // Dimensions accordion row (their own carousel + photo viewer).
    const rawStack = getGallerySlides({
        LibraryImages: product.LibraryImages,
        LibraryVideos: product.LibraryVideos,
        DiagramImages: [],
    });
    // Open the carousel on the image the user clicked: tile grids and the
    // gallery order their images independently, so the clicked image is often
    // not the gallery's first slide — the morph would land on it and then
    // dissolve to a different photo. ROTATE the stack (not reorder — the
    // carousel loops, so cyclic order is unchanged) so slide 0, which carries
    // the morph target, underlay and dissolve, IS the clicked image.
    const heroKey = heroUnderlaySrc ? galleryImageKey(heroUnderlaySrc) : null;
    const heroSlideIndex = heroKey
        ? rawStack.findIndex(
              (slide) =>
                  !isLibraryVideo(slide) &&
                  galleryImageKey(
                      slide.LibraryImageFile?.DetailPageNatural?.Desktop ?? '',
                  ) === heroKey,
          )
        : -1;
    const stack =
        heroSlideIndex > 0
            ? [
                  ...rawStack.slice(heroSlideIndex),
                  ...rawStack.slice(0, heroSlideIndex),
              ]
            : rawStack;

    // Photo-viewer CTA parity with the legacy gallery: tracked website link +
    // the standard product enquiry modal.
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(product.Website || professional.Website, {
            libraryItemID: product.ID,
            professionalID: professional.ID,
            professionalTitle: professional.Title,
        });
    const { openEnquiryModal } = useProductEnquiryModal();

    // Drive the Magic Island's pagination mode from the gallery's own carousel.
    // The section owns the embla api (via setApi), so it registers directly
    // rather than through useIslandCarousel — the in-view ref then sits on the
    // full-height <section>. Single-image galleries register nothing.
    // The autoplay clock lives here, not in the island's progress fill — so
    // scrolling (which unmounts the island, and any in-flight CSS fill with it)
    // can't drop a pending advance or reset the dwell, which is what made the
    // carousel stick while the island flickered. The island's fill is a visual
    // indicator only; `advance` runs on the timer below. `remainingDwellRef`
    // carries the dwell left on the current slide so pause/resume stays in step.
    const [isPlaying, setIsPlaying] = useState(true);
    const remainingDwellRef = useRef(GALLERY_AUTOPLAY_DELAY);
    // The autoplay clock only runs while the island is actually on screen — the
    // island reports its mount/unmount here (and stays silent under reduced
    // motion). The banked dwell means the flicker during scroll resumes, not
    // resets.
    const [islandActive, setIslandActive] = useState(false);
    const handleIslandVisible = useCallback(
        (visible: boolean) => setIslandActive(visible),
        [],
    );
    const togglePlay = useCallback(
        () => setIsPlaying((playing) => !playing),
        [],
    );
    const advance = useCallback(() => {
        if (!emblaApi) return;
        // Stop on the last slide instead of looping back, so the island swaps
        // its play/pause control for a replay button (see PaginationIslandContent).
        // Read the snap count live from embla (not a captured stack.length) so
        // client-side navigation between products can't leave a stale length that
        // makes a shorter gallery loop past its end.
        if (
            emblaApi.selectedScrollSnap() >=
            emblaApi.scrollSnapList().length - 1
        ) {
            setIsPlaying(false);
            setReachedEnd(true);
            return;
        }
        emblaApi.scrollNext();
    }, [emblaApi]);
    const replay = useCallback(() => {
        if (!emblaApi) return;
        setReachedEnd(false);
        setIsPlaying(true);
        emblaApi.scrollTo(0);
    }, [emblaApi]);

    // A fresh slide gets the full dwell. (Declared before the timer effect so
    // its reset wins over the timer's elapsed-bookkeeping on a slide change.)
    useEffect(() => {
        remainingDwellRef.current = GALLERY_AUTOPLAY_DELAY;
    }, [currentSlide]);
    // The autoplay timer: advance after the remaining dwell while playing and
    // not yet ended; on pause/unschedule, bank the elapsed so resume continues
    // from where it left off (matching the island's frozen fill).
    useEffect(() => {
        if (!emblaApi || !isPlaying || reachedEnd || !islandActive) return;
        const startedAt = performance.now();
        const timer = window.setTimeout(advance, remainingDwellRef.current);
        return () => {
            window.clearTimeout(timer);
            remainingDwellRef.current = Math.max(
                0,
                remainingDwellRef.current - (performance.now() - startedAt),
            );
        };
    }, [emblaApi, isPlaying, reachedEnd, islandActive, currentSlide, advance]);

    const islandMode = useMemo<IslandMode>(
        () => ({
            kind: 'pagination',
            label: 'Product gallery',
            total: stack.length,
            activeIndex: currentSlide,
            onSelect: (index) => emblaApi?.scrollTo(index),
            isPlaying,
            onTogglePlay: togglePlay,
            autoplayDelayMs: GALLERY_AUTOPLAY_DELAY,
            onReplay: replay,
            atEnd: reachedEnd,
            onVisible: handleIslandVisible,
        }),
        [
            stack.length,
            currentSlide,
            emblaApi,
            isPlaying,
            togglePlay,
            replay,
            reachedEnd,
            handleIslandVisible,
        ],
    );
    const islandRef = useIslandSection<HTMLElement>(islandMode, {
        // Desktop only: the island's gallery pagination drives the centred
        // desktop carousel. Mobile is a plain swipe row (the island pagination
        // was unreliable there), so it registers nothing and the purchase pill
        // stays the island's mobile content.
        enabled: !mobile && stack.length > 1,
        // Above link/cta default (0) so gallery pagination sticks while peeking.
        priority: 1,
    });

    if (stack.length === 0) return null;

    // Slide 0's width at SSR (height × its clamped ratio) so the centred hero
    // is pre-positioned correctly and doesn't jump when embla hydrates — one
    // per breakpoint (rem on desktop, vw on mobile).
    const firstSlide = stack[0];
    const firstSlideRatio = firstSlide ? getSlideRatio(firstSlide) : 1;
    const firstSlideWidthRem = GALLERY_SLIDE_HEIGHT_REM * firstSlideRatio;
    const isMulti = stack.length > 1;
    // A lone clear-cut (multiplied) landscape/square image extends to the page
    // content gutter as one wide band: the product stays contained at natural
    // size and the neutral fill spans both gutters. Not a proportional upscale,
    // and no carousel needed. Portraits keep the standard centred card.
    const singleClearCutWide =
        !isMulti &&
        !!firstSlide &&
        !isLibraryVideo(firstSlide) &&
        !!firstSlide.IsMultiplied &&
        firstSlideRatio >= 1;
    // Mobile viewport gutter: always the 1rem page gutter. A lone slide
    // stretches to fill the width inside it (see the CarouselItem width)
    // rather than centring at its ratio width — a narrow floating card with
    // deep side gutters reads unbalanced, especially in app-mode where the
    // top pad matches this gutter.
    const mobileViewportPad = '1rem';

    const trackViewerOpen = () => {
        tracker.log('PhotoViewerTrigger', {
            url: new URL(window.location.href),
            data: { LibraryItemID: product.ID || 0 },
        });
    };

    const openViewerAt = (index: number) => {
        trackViewerOpen();
        setActiveIndex(index);
        setViewerOpen(true);
    };

    /** Open the mobile viewer's Photos tab on this image. */
    const openMobileViewerAt = (slideId: number) => {
        trackViewerOpen();
        openMobileViewer({ tab: 'photos', slideId });
    };

    // Centre slide + both peeks are visible on load (the carousel loops, so
    // the last slide is the initial left peek); beyond that, eager-load two
    // slides either side of wherever the carousel has settled so neighbours
    // are painted before they're dragged into view.
    const isEagerSlide = (index: number) => {
        const count = stack.length;
        if (index <= 1 || index === count - 1) return true;
        const distance = Math.min(
            Math.abs(index - currentSlide),
            count - Math.abs(index - currentSlide),
        );
        return distance <= 2;
    };

    if (singleClearCutWide && firstSlide && !isLibraryVideo(firstSlide)) {
        const heroSrc =
            firstSlide.LibraryImageFile?.DetailPageNatural?.Desktop ?? '';
        // Identical either side of the breakpoint split below — only the viewer
        // the surrounding button opens differs — so it's built once.
        const heroImage = (
            <Image
                src={heroSrc}
                alt={product.Title}
                className={cn(
                    heroUnderlaySrc && 'transition-opacity duration-300',
                    heroUnderlaySrc && !heroImageLoaded && 'opacity-0',
                )}
                onLoad={heroUnderlaySrc ? markHeroImageLoaded : undefined}
                fill
                fit="contain"
                multiplied
                sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                loading="eager"
                priority
                fetchPriority="high"
                fadeIn={false}
            />
        );
        return (
            <section
                ref={islandRef}
                className="relative w-full overflow-x-clip bg-white"
            >
                {/* Single clear-cut hero: full-width band aligned to the page
                    content gutter (md:px-[3rem]); the product sits contained on
                    the neutral fill, which extends laterally to both gutters. */}
                <div className="md:px-[3rem]">
                    {/* Mobile stretches to the 1rem page gutter (matching the
                        carousel's lone-slide sizing); desktop extends to the
                        full content width. This band is what the carousel's
                        slide 0 is in the multi-image layout — the listing
                        tile's counterpart — so it carries the same shared
                        snapshot name and cold-cache underlay. */}
                    <div
                        className="relative mx-auto h-[84vw] w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-[var(--color-clearcut-fill)] md:mx-0 md:h-[54.375rem] md:w-full"
                        style={heroStyle}
                    >
                        {heroUnderlaySrc ? (
                            <img
                                src={heroUnderlaySrc}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 size-full object-contain mix-blend-multiply"
                            />
                        ) : null}
                        {mobile ? (
                            <button
                                type="button"
                                onClick={() =>
                                    openMobileViewerAt(firstSlide.ID)
                                }
                                className="absolute inset-0"
                                aria-label={`View ${product.Title} image 1 full screen`}
                            >
                                {heroImage}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => openViewerAt(0)}
                                className="absolute inset-0 cursor-zoom-in"
                                aria-label={`View ${product.Title} image 1 full screen`}
                            >
                                {heroImage}
                            </button>
                        )}
                        {mobile ? (
                            <TileOverlayWithFollowSelector
                                pinId={firstSlide.ID}
                                libraryItemId={product.ID}
                                pinType="PhotoPin"
                                mobileOnly
                            />
                        ) : null}

                        {/* Award badges, bottom-left inside the hero band
                            (flag-gated), matching the carousel gallery's
                            desktop-only placement. */}
                        {enableAwardBadges && product.Awards.length > 0 ? (
                            <div className="absolute z-10 max-md:hidden md:bottom-[1.44rem] md:left-[6.6rem]">
                                <BannerAwards
                                    awards={product.Awards}
                                    awardsPerSlide={5}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>

                {viewerOpen ? (
                    <Suspense fallback={null}>
                        <PhotoViewerModal
                            key={product.ID}
                            stack={stack}
                            selectedSlide={activeIndex}
                            open={viewerOpen}
                            toggle={() => setViewerOpen((current) => !current)}
                            itemID={product.ID}
                            title={product.Title}
                            cta={{
                                websiteUrl: referralTrackingURL ?? undefined,
                                ...(referralTrackingURL && {
                                    websiteTrackingParams:
                                        referralTrackingParams,
                                }),
                                onEnquire: () =>
                                    openEnquiryModal({
                                        title: 'Enquire',
                                        ctaLabel: 'Enquire',
                                    }),
                                enquiryLabel: 'Enquire',
                            }}
                            onSlideChange={(stackIndex) => {
                                setActiveIndex(stackIndex);
                            }}
                        />
                    </Suspense>
                ) : null}
            </section>
        );
    }

    return (
        <section
            ref={islandRef}
            className="relative w-full overflow-x-clip bg-white"
            style={{ '--g-pad': mobileViewportPad } as CSSProperties}
        >
            {/* Award badges, bottom-left inside the main image (flag-gated). */}
            {enableAwardBadges && product.Awards.length > 0 ? (
                <div className="absolute z-10 max-md:hidden md:top-[52.935rem] md:left-[9.6rem] md:-translate-y-full">
                    <BannerAwards awards={product.Awards} awardsPerSlide={5} />
                </div>
            ) : null}

            <Carousel
                setApi={setEmblaApi}
                ariaLabel={`${product.Title} photo gallery`}
                options={{
                    align: 'center',
                    // Slower, more deliberate slide (embla's scroll physics is a
                    // friction-based ease-out — see the easing note on the PR).
                    duration: 32,
                    containScroll: false,
                    startIndex: 0,
                    // Desktop: when the island is on it drives navigation, so
                    // disable mouse drag (it otherwise fights the active state).
                    // Without the island (revamp-only), keep the original
                    // free-drag so nothing is lost.
                    watchDrag: !islandEnabled,
                    dragFree: !islandEnabled,
                    loop: isMulti,
                    // Mobile aligns the row to the page gutter (first slide's
                    // left edge, last slide's right edge, via trimSnaps) with
                    // the next slide peeking; a lone slide just centres. Keeps
                    // native swipe — the island drives desktop, so drag is
                    // otherwise off there.
                    breakpoints: {
                        '(max-width: 767px)': {
                            align: 'start',
                            containScroll: isMulti ? 'trimSnaps' : false,
                            // No loop on mobile: a finite row so the last slide
                            // snaps to the right gutter (trimSnaps) instead of
                            // the first wrapping in with no trailing gap.
                            loop: false,
                            watchDrag: true,
                            dragFree: true,
                        },
                    },
                }}
            >
                {/* Pre-centre slide 0 at SSR so the hero doesn't jump from
                    flush-left to centred when embla hydrates: embla's resting
                    transform for a centre-aligned slide 0 is exactly
                    (viewport - slide)/2, which this matches, so embla overrides
                    it with an identical value on init. Desktop only — mobile is
                    align:start, already flush-left correct. */}
                <CarouselContent
                    // Mobile gutter lives on the embla viewport so the first /
                    // last snaps land on the page gutter while slides scroll
                    // through it; the inter-slide gap is a track gap (no item
                    // padding to offset that gutter).
                    // Mobile side padding centres a lone slide / gutters a row
                    // (see --g-pad); slides scroll through it, first / last snap
                    // to the gutter via trimSnaps.
                    viewportClassName="max-md:px-[var(--g-pad)]"
                    className={cn(
                        'max-md:gap-3',
                        'md:[transform:translateX(calc((100%_-_var(--g-slide0-w))/2))]',
                    )}
                    style={
                        {
                            '--g-slide0-w': `${firstSlideWidthRem}rem`,
                        } as CSSProperties
                    }
                >
                    {stack.map((slide, index) => (
                        <CarouselItem
                            key={`${slide.ID}-${index}`}
                            className="shrink-0 grow-0 basis-auto px-0 md:px-[0.78rem]"
                            style={
                                {
                                    '--g-slide-w': `${GALLERY_SLIDE_HEIGHT_REM * getSlideRatio(slide)}rem`,
                                    // A lone slide fills the width inside the
                                    // page gutter; clear-cut renders gain
                                    // canvas (object-contain), photos take a
                                    // mild cover-crop.
                                    '--g-slide-w-m': isMulti
                                        ? `${mobileSlideWidthVw(getSlideRatio(slide))}vw`
                                        : 'calc(100vw - 2rem)',
                                } as CSSProperties
                            }
                        >
                            {/* Variable-width, consistent-height card: the
                                height is fixed and the width follows the image
                                ratio, so the row keeps one height. Square and
                                portrait fill their card; on mobile a landscape
                                is clamped to a square card and cropped to fill
                                (no letterbox). Cards sit a little inside the
                                viewport so the next slide peeks. */}
                            <div
                                className="relative h-[84vw] w-[var(--g-slide-w-m)] overflow-hidden rounded-xl bg-[var(--color-clearcut-fill)] md:h-[54.375rem] md:w-[var(--g-slide-w)]"
                                style={index === 0 ? heroStyle : undefined}
                            >
                                {index === 0 &&
                                heroUnderlaySrc &&
                                !isLibraryVideo(slide) ? (
                                    <img
                                        src={heroUnderlaySrc}
                                        alt=""
                                        aria-hidden="true"
                                        className={cn(
                                            'absolute inset-0 size-full',
                                            slide.IsMultiplied
                                                ? 'object-contain mix-blend-multiply'
                                                : 'object-cover',
                                        )}
                                    />
                                ) : null}
                                {isLibraryVideo(slide) ? (
                                    <GalleryVideoSlide slide={slide} />
                                ) : mobile ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            openMobileViewerAt(slide.ID)
                                        }
                                        className="absolute inset-0"
                                        aria-label={`View ${product.Title} image ${index + 1} full screen`}
                                    >
                                        <Image
                                            src={
                                                slide.LibraryImageFile
                                                    ?.DetailPageNatural
                                                    ?.Desktop ?? ''
                                            }
                                            alt={product.Title}
                                            className={cn(
                                                index === 0 &&
                                                    heroUnderlaySrc &&
                                                    'transition-opacity duration-300',
                                                index === 0 &&
                                                    heroUnderlaySrc &&
                                                    !heroImageLoaded &&
                                                    'opacity-0',
                                            )}
                                            onLoad={
                                                index === 0 && heroUnderlaySrc
                                                    ? markHeroImageLoaded
                                                    : undefined
                                            }
                                            fill
                                            // Photos fill the card (a landscape
                                            // crops to the square); clear-cut
                                            // product shots stay contained so the
                                            // whole product shows.
                                            fit={
                                                slide.IsMultiplied
                                                    ? 'contain'
                                                    : 'cover'
                                            }
                                            multiplied={Boolean(
                                                slide.IsMultiplied,
                                            )}
                                            sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                                            loading={
                                                isEagerSlide(index)
                                                    ? 'eager'
                                                    : 'lazy'
                                            }
                                            priority={index === 0}
                                            fetchPriority={
                                                index === 0 ? 'high' : undefined
                                            }
                                            fadeIn={false}
                                        />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => openViewerAt(index)}
                                        className="absolute inset-0 cursor-zoom-in"
                                        aria-label={`View ${product.Title} image ${index + 1} full screen`}
                                    >
                                        {/* Clear-cut images (IsMultiplied)
                                            sit contained on the grey fill and
                                            multiply into it, per the legacy
                                            gallery; photos keep the
                                            full-bleed crop. */}
                                        <Image
                                            src={
                                                slide.LibraryImageFile
                                                    ?.DetailPageNatural
                                                    ?.Desktop ?? ''
                                            }
                                            alt={product.Title}
                                            className={cn(
                                                index === 0 &&
                                                    heroUnderlaySrc &&
                                                    'transition-opacity duration-300',
                                                index === 0 &&
                                                    heroUnderlaySrc &&
                                                    !heroImageLoaded &&
                                                    'opacity-0',
                                            )}
                                            onLoad={
                                                index === 0 && heroUnderlaySrc
                                                    ? markHeroImageLoaded
                                                    : undefined
                                            }
                                            fill
                                            fit={
                                                slide.IsMultiplied
                                                    ? 'contain'
                                                    : 'cover'
                                            }
                                            multiplied={Boolean(
                                                slide.IsMultiplied,
                                            )}
                                            sizes={PRODUCT_GALLERY_SLIDE_SIZES}
                                            loading={
                                                isEagerSlide(index)
                                                    ? 'eager'
                                                    : 'lazy'
                                            }
                                            priority={index === 0}
                                            fetchPriority={
                                                index === 0 ? 'high' : undefined
                                            }
                                            fadeIn={false}
                                        />
                                    </button>
                                )}
                                {/* Save (design-board / FollowSelector) heart
                                    on the image — mobile only; on desktop the
                                    hover overlay carries the same actions.
                                    Saves the specific image (PhotoPin, keyed by
                                    the LibraryImage id) rather than the product
                                    — the product itself is saved from the CTA
                                    group's DetailSaveButton. Matches the legacy
                                    gallery's per-slide pin config. */}
                                {mobile && !isLibraryVideo(slide) ? (
                                    <TileOverlayWithFollowSelector
                                        pinId={slide.ID}
                                        libraryItemId={product.ID}
                                        pinType="PhotoPin"
                                        mobileOnly
                                    />
                                ) : null}
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <GalleryPagination />
            </Carousel>

            {viewerOpen ? (
                <Suspense fallback={null}>
                    <PhotoViewerModal
                        key={product.ID}
                        stack={stack}
                        selectedSlide={activeIndex}
                        open={viewerOpen}
                        toggle={() => setViewerOpen((current) => !current)}
                        itemID={product.ID}
                        title={product.Title}
                        cta={{
                            websiteUrl: referralTrackingURL ?? undefined,
                            ...(referralTrackingURL && {
                                websiteTrackingParams: referralTrackingParams,
                            }),
                            onEnquire: () =>
                                openEnquiryModal({
                                    title: 'Enquire',
                                    ctaLabel: 'Enquire',
                                }),
                            enquiryLabel: 'Enquire',
                        }}
                        onSlideChange={(stackIndex) => {
                            setActiveIndex(stackIndex);
                            emblaApi?.scrollTo(stackIndex);
                        }}
                    />
                </Suspense>
            ) : null}
        </section>
    );
};
